/* SpawnAnything — battle statistics UI (feature:stats).
   Live box in the battle HUD (kills/s, strength ratio, collapsible), "Statistik ansehen" on the victory banner and a
   modal with: army strength over time (SVG line chart), damage / kills by source (bars), death zones (heatmap over a
   top-down map) and tiles. Data: compact packets from the sim worker (src/sim/features/stats.js), about 2× per second. */
import './stats.css';
import { decodeStats, HG, SRC_WEAPON, SRC_BUILDING, SRC_TOOL, SRC_STOMP, SRC_FALL } from '../sim/features/stats.js';
import { cellAt, inBounds } from '../world/terrain.js';

const nf = new Intl.NumberFormat('de-DE');
const nf1 = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 1 });
const nfc = new Intl.NumberFormat('de-DE', { notation: 'compact', maximumFractionDigits: 1 });
const fmt = n => nf.format(Math.round(n));
const clock = t => `${Math.floor(t / 60)}:${String(Math.floor(t % 60)).padStart(2, '0')}`;
const esc = v => String(v).replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);
const TEAM = ['Blau', 'Rot'];
const TC = ['#5a93f0', '#e85a4e'];       // validated pair on the dark panel (lines and bars)
const GOD = '#d39a1c', ENV = '#8a93a3';  // single bars without a team: god tools, falls
const SRC = [
  { k: SRC_WEAPON, name: 'Waffen', team: true },
  { k: SRC_BUILDING, name: 'Gebäude & Geschütze', team: true },
  { k: SRC_STOMP, name: 'Spezial', team: true },
  { k: SRC_TOOL, name: 'Gott-Werkzeuge', team: false, who: 'Gott', c: GOD },
  { k: SRC_FALL, name: 'Fallschaden', team: false, who: 'Sturz', c: ENV },
];
const store = {
  get(k) { try { return localStorage.getItem(k); } catch (e) { return null; } },
  set(k, v) { try { localStorage.setItem(k, v); } catch (e) { /* private mode */ } },
};
const h = (tag, cls, html) => { const e = document.createElement(tag); if (cls) e.className = cls; if (html != null) e.innerHTML = html; return e; };

/**
 * @param {object} o
 * @param {() => Array} o.armies      current armies (buildArmy results: ty, wp, stats, reserve)
 * @param {() => object|null} o.terrain  terrain object or null (flat field)
 * @param {() => Array} o.buildings   building sim data ({ x, z, hw, hd, team })
 * @param {(i: number) => boolean} o.focusUnit  move the camera to unit i; false when it is gone
 */
export function createBattleStats(o) {
  let D = null, hist = [], mode = 'dealt', heatTeam = -1, open = false, collapsed = store.get('aw-stats-live') === '0';

  /* ---- live box ---- */
  const live = h('div', 'stats-live');
  live.hidden = true;
  live.innerHTML = `<button class="stats-live-h" type="button" aria-expanded="true"><span>Live-Statistik</span><i aria-hidden="true"></i></button>
    <div class="stats-live-b"><div class="stats-live-k"><b>0</b><span>Kills/s</span></div>
    <div class="stats-ratio" title="Kräfteverhältnis (lebende Einheiten)"><i></i><i></i></div>
    <div class="stats-live-r"><span></span><span></span></div>
    <div class="stats-live-btns"><button class="stats-live-more" type="button">Details</button><button class="stats-live-back" type="button" hidden>Ergebnis</button></div></div>`;
  document.body.appendChild(live);
  const lHead = live.querySelector('.stats-live-h'), lBody = live.querySelector('.stats-live-b');
  const lK = live.querySelector('.stats-live-k b'), lBars = live.querySelectorAll('.stats-ratio i'), lR = live.querySelectorAll('.stats-live-r span');
  const setCollapsed = c => { collapsed = c; lBody.hidden = c; lHead.setAttribute('aria-expanded', String(!c)); live.classList.toggle('stats-closed', c); store.set('aw-stats-live', c ? '0' : '1'); };
  setCollapsed(collapsed);
  lHead.addEventListener('click', () => setCollapsed(!collapsed));
  live.querySelector('.stats-live-more').addEventListener('click', () => show());
  // the camera flight hides the victory banner; this brings it back (rematch etc.)
  const lBack = live.querySelector('.stats-live-back');
  const setBack = on => { lBack.hidden = !on; const b = document.querySelector('#banner'); if (b && !on && lBack.dataset.on) b.hidden = false; lBack.dataset.on = on ? '1' : ''; };
  lBack.addEventListener('click', () => setBack(false));

  /* ---- banner button ---- */
  const bb = document.querySelector('#banner .bbtn');
  const bannerBtn = h('button', 'stats-open', 'Statistik ansehen');
  bannerBtn.type = 'button';
  if (bb) bb.appendChild(bannerBtn);
  bannerBtn.addEventListener('click', () => show());

  /* ---- modal (built on first use) ---- */
  let M = null;
  function build() {
    M = h('div', 'modal stats-modal');
    M.hidden = true;
    M.innerHTML = `<div class="mbox stats-box" role="dialog" aria-modal="true" aria-labelledby="statsTitle">
      <div class="mhead"><b id="statsTitle">Kampf-Statistik</b><button class="ghost stats-close" type="button">Schließen</button></div>
      <div class="stats-tiles"></div>
      <section class="stats-card"><div class="stats-ch"><span class="stats-h">Armeestärke</span><span class="stats-leg"><i style="background:${TC[0]}"></i>Blau<i style="background:${TC[1]}"></i>Rot</span></div>
        <div class="stats-line"><svg viewBox="0 0 640 220" role="img" aria-label="Lebende Einheiten über die Zeit"></svg><div class="stats-tip" hidden></div></div></section>
      <div class="stats-grid">
        <section class="stats-card"><div class="stats-ch"><span class="stats-h">Je Quelle</span>
          <div class="seg stats-seg" role="group" aria-label="Was zeigen?"><button data-m="dealt">Ausgeteilt</button><button data-m="taken">Erlitten</button><button data-m="kills">Kills</button></div></div>
          <div class="stats-bars"></div><p class="stats-note"></p></section>
        <section class="stats-card"><div class="stats-ch"><span class="stats-h">Todeszonen</span>
          <div class="seg stats-seg" role="group" aria-label="Gefallene welches Teams?"><button data-h="-1">Alle</button><button data-h="0" class="tb0">Blau</button><button data-h="1" class="tb1">Rot</button></div></div>
          <div class="stats-map"><canvas width="320" height="320" aria-label="Karte der Gefallenen von oben"></canvas></div>
          <div class="stats-ramp"><span>wenige</span><i></i><span class="stats-max"></span></div><p class="stats-note stats-scale"></p></section>
      </div>
      <div class="stats-foot"><button class="go stats-focus" type="button">Kamera zur tödlichsten Einheit</button></div>
    </div>`;
    document.body.appendChild(M);
    M.querySelector('.stats-close').addEventListener('click', hide);
    M.addEventListener('pointerdown', e => { if (e.target === M) hide(); });
    M.querySelectorAll('[data-m]').forEach(b => b.addEventListener('click', () => { mode = b.dataset.m; render(); }));
    M.querySelectorAll('[data-h]').forEach(b => b.addEventListener('click', () => { heatTeam = +b.dataset.h; render(); }));
    M.querySelector('.stats-focus').addEventListener('click', () => {
      if (D && D.best.i >= 0 && D.best.alive && o.focusUnit(D.best.i)) {
        hide();
        const b = document.querySelector('#banner');
        if (b && !b.hidden) { b.hidden = true; setBack(true); }
        if (collapsed) setCollapsed(false);
      }
      else render();
    });
    const svg = M.querySelector('svg');
    svg.addEventListener('pointermove', e => hover(e));
    svg.addEventListener('pointerleave', () => hover(null));
  }
  // while open, the modal owns the keyboard (main.js would pause, switch tools, ...)
  addEventListener('keydown', e => {
    if (!open) return;
    e.stopPropagation();
    if (e.code === 'Escape') hide();
  }, true);
  addEventListener('resize', () => { if (open) render(); });
  function show() {
    if (!D) return;
    if (!M) build();
    open = true; M.hidden = false; render();
    M.querySelector('.stats-close').focus();
  }
  function hide() { open = false; if (M) M.hidden = true; }

  /* ---- data in ---- */
  function update(buf) {
    D = decodeStats(buf);
    const t = D.t, dead = D.teams[0].dead + D.teams[1].dead;
    hist.push(t, dead);
    while (hist.length > 2 && t - hist[0] > 2.5) hist.splice(0, 2);
    const dt = t - hist[0];
    const kps = dt > 0.2 ? (dead - hist[1]) / dt : 0;
    const a0 = D.teams[0].alive, a1 = D.teams[1].alive, sum = a0 + a1 || 1;
    lK.textContent = D.end >= 0 ? '–' : nf1.format(kps);
    lBars[0].style.width = (a0 / sum * 100) + '%'; lBars[1].style.width = (a1 / sum * 100) + '%';
    lR[0].textContent = `Blau ${Math.round(a0 / sum * 100)} %`; lR[1].textContent = `Rot ${Math.round(a1 / sum * 100)} %`;
    live.hidden = false; bannerBtn.disabled = false;
    if (open) render();
  }
  function reset() {
    D = null; hist = []; live.hidden = true; bannerBtn.disabled = true; hide();
    lBack.hidden = true; lBack.dataset.on = '';
  }
  reset();

  /* ---- rendering ---- */
  function unitName(i, t) {
    const A = o.armies().find(G => G && i >= G.start && i < G.end); if (!A) return TEAM[t]; // the unit's group
    return `${A.ty.name} Nr. ${fmt(i - A.start + 1)}`;
  }
  function render() {
    if (!M || !D) return;
    M.querySelectorAll('[data-m]').forEach(b => b.setAttribute('aria-pressed', String(b.dataset.m === mode)));
    M.querySelectorAll('[data-h]').forEach(b => b.setAttribute('aria-pressed', String(+b.dataset.h === heatTeam)));
    tiles(); line(); bars(); heat();
    const fb = M.querySelector('.stats-focus'), ok = D.best.i >= 0 && D.best.alive;
    fb.disabled = !ok;
    fb.title = D.best.i < 0 ? 'Noch hat keine Einheit getötet.' : ok ? '' : 'Die tödlichste Einheit ist gefallen.';
  }
  function tiles() {
    const T = D.teams, dur = D.end >= 0 ? D.end : D.t, b = D.best;
    const dot = t => `<i class="stats-dot" style="background:${TC[t]}"></i>`;
    const blost = T[0].blost + T[1].blost;
    const lng = t => {
      const L = T[t].longest;
      if (L.i < 0) return `${dot(t)}–`;
      return `${dot(t)}${esc(unitName(L.i, t))} · ${clock(L.life)}${L.alive ? '' : ' (gefallen)'}`;
    };
    M.querySelector('.stats-tiles').innerHTML = `
      <div class="stats-tile"><span>Kampfdauer</span><b>${clock(dur)}</b><small>${D.end >= 0 ? 'Schlacht entschieden' : 'läuft noch'}</small></div>
      <div class="stats-tile"><span>Verluste</span><b>${dot(0)}${fmt(T[0].dead)} <em>·</em> ${dot(1)}${fmt(T[1].dead)}</b><small>${blost ? `Gebäude zerstört: Blau ${fmt(T[0].blost)} · Rot ${fmt(T[1].blost)}` : `von ${fmt(T[0].start)} und ${fmt(T[1].start)} zu Beginn`}</small></div>
      <div class="stats-tile"><span>Tödlichste Einheit</span><b>${b.i >= 0 ? `${dot(b.team)}${fmt(b.kills)} Kills` : '–'}</b><small>${b.i >= 0 ? `${esc(unitName(b.i, b.team))} · ${b.alive ? 'lebt noch' : 'gefallen'}` : 'Noch keine Kills einer Einheit'}</small></div>
      <div class="stats-tile"><span>Explosionen</span><b>${fmt(D.expl)}</b><small>Geschosse und Werkzeuge</small></div>
      <div class="stats-tile stats-wide"><span>Am längsten am Leben</span><small>${lng(0)}</small><small>${lng(1)}</small></div>`;
  }

  /* line chart: alive units per team over time */
  const PL = 44, PR = 70, PT = 12, PB = 26;
  let LW = 640, LH = 220; // viewBox follows the rendered width so text stays at its CSS size
  let geo = null;
  function niceStep(max, n) {
    const raw = max / n, p = Math.pow(10, Math.floor(Math.log10(raw || 1))), f = raw / p;
    return (f <= 1 ? 1 : f <= 2 ? 2 : f <= 5 ? 5 : 10) * p;
  }
  function line() {
    const svg = M.querySelector('svg'), ns = D.ns, tl = D.tl;
    LW = Math.max(300, Math.round(svg.parentNode.clientWidth || 640)); LH = Math.round(Math.min(240, Math.max(170, LW * 0.34)));
    svg.setAttribute('viewBox', `0 0 ${LW} ${LH}`);
    const tAt = k => (D.end >= 0 && k === ns - 1 ? D.end : k * D.dt);
    const tMax = Math.max(1, ns ? tAt(ns - 1) : 1);
    let yMax = 1; for (let k = 0; k < ns * 2; k++) if (tl[k] > yMax) yMax = tl[k];
    const ys = niceStep(yMax, 4), yTop = Math.ceil(yMax / ys) * ys;
    const X = t => PL + (t / tMax) * (LW - PL - PR), Y = v => PT + (1 - v / yTop) * (LH - PT - PB);
    let s = '';
    for (let v = 0; v <= yTop + 1e-9; v += ys) s += `<line class="stats-grid-l" x1="${PL}" x2="${LW - PR}" y1="${Y(v)}" y2="${Y(v)}"/><text class="stats-ax" x="${PL - 6}" y="${Y(v) + 4}" text-anchor="end">${nfc.format(v)}</text>`;
    const xs = [5, 10, 15, 30, 60, 120, 300, 600, 1200, 1800].find(v => tMax / v <= Math.max(3, Math.floor(LW / 90))) || 3600;
    for (let t = 0; t <= tMax + 1e-9; t += xs) s += `<text class="stats-ax" x="${X(t)}" y="${LH - 6}" text-anchor="middle">${clock(t)}</text>`;
    for (let t = 0; t < 2; t++) {
      if (!ns) break;
      let d = '';
      for (let k = 0; k < ns; k++) d += (k ? 'L' : 'M') + X(tAt(k)).toFixed(1) + ' ' + Y(tl[k * 2 + t]).toFixed(1);
      s += `<path d="${d}" fill="none" stroke="${TC[t]}" stroke-width="2" stroke-linejoin="round" stroke-linecap="round"/>`;
    }
    if (ns) {
      // direct labels at the line ends, nudged apart when they collide
      let y0 = Y(tl[(ns - 1) * 2]), y1 = Y(tl[(ns - 1) * 2 + 1]);
      if (Math.abs(y0 - y1) < 14) { const m = (y0 + y1) / 2, up = y0 <= y1 ? -7 : 7; y0 = m + up; y1 = m - up; }
      const ex = X(tMax) + 8;
      s += `<text class="stats-end" x="${ex}" y="${y0 + 4}">Blau ${fmt(tl[(ns - 1) * 2])}</text><text class="stats-end" x="${ex}" y="${y1 + 4}">Rot ${fmt(tl[(ns - 1) * 2 + 1])}</text>`;
    }
    s += `<line class="stats-cross" x1="0" x2="0" y1="${PT}" y2="${LH - PB}" visibility="hidden"/><circle class="stats-pt" r="4" fill="${TC[0]}" visibility="hidden"/><circle class="stats-pt" r="4" fill="${TC[1]}" visibility="hidden"/>`;
    svg.innerHTML = s;
    geo = { X, Y, tAt, tMax, ns };
  }
  function hover(e) {
    if (!geo || !M) return;
    const svg = M.querySelector('svg'), tip = M.querySelector('.stats-tip');
    const cross = svg.querySelector('.stats-cross'), pts = svg.querySelectorAll('.stats-pt');
    const hideAll = () => { tip.hidden = true; cross.setAttribute('visibility', 'hidden'); pts.forEach(p => p.setAttribute('visibility', 'hidden')); };
    if (!e || !geo.ns) { hideAll(); return; }
    const r = svg.getBoundingClientRect(), x = (e.clientX - r.left) / r.width * LW;
    const t = (x - PL) / (LW - PL - PR) * geo.tMax;
    if (t < -geo.tMax * 0.02 || t > geo.tMax * 1.02) { hideAll(); return; }
    let k = Math.round(Math.max(0, t) / D.dt); if (k > geo.ns - 1) k = geo.ns - 1;
    const tx = geo.X(geo.tAt(k)), v0 = D.tl[k * 2], v1 = D.tl[k * 2 + 1];
    cross.setAttribute('x1', tx); cross.setAttribute('x2', tx); cross.setAttribute('visibility', 'visible');
    [v0, v1].forEach((v, i) => { pts[i].setAttribute('cx', tx); pts[i].setAttribute('cy', geo.Y(v)); pts[i].setAttribute('visibility', 'visible'); });
    tip.innerHTML = `<b>${clock(geo.tAt(k))}</b><span><i style="background:${TC[0]}"></i>Blau ${fmt(v0)}</span><span><i style="background:${TC[1]}"></i>Rot ${fmt(v1)}</span>`;
    tip.hidden = false;
    const px = tx / LW * r.width;
    tip.style.left = Math.min(r.width - 120, Math.max(0, px + 10)) + 'px';
  }

  /* bars: damage dealt / taken or kills by source */
  function bars() {
    const T = D.teams, A = o.armies();
    const val = (t, k) => mode === 'taken' ? T[t].dmg[k] : mode === 'dealt' ? T[1 - t].dmg[k] : T[1 - t].kill[k];
    const tot = k => mode === 'kills' ? T[0].kill[k] + T[1].kill[k] : T[0].dmg[k] + T[1].dmg[k];
    const rows = [];
    let max = 0;
    for (const S of SRC) {
      const split = S.team || mode === 'taken';
      const v = split ? [val(0, S.k), val(1, S.k)] : [tot(S.k)];
      if (S.k !== SRC_WEAPON && !(v[0] || v[1])) continue;
      for (const x of v) if (x > max) max = x;
      rows.push({ S, v, split });
    }
    const num = v => mode === 'kills' ? fmt(v) : nfc.format(Math.round(v));
    const unit = mode === 'kills' ? 'Kills' : 'Schaden';
    const w = v => max > 0 ? Math.max(v > 0 ? 1.5 : 0, v / max * 100) : 0;
    const wn = A[0] && A[1] ? `${esc(A[0].wp.name)} · ${esc(A[1].wp.name)}` : '';
    let s = '';
    for (const { S, v, split } of rows) {
      s += `<div class="stats-row"><div class="stats-rl">${S.name}${S.k === SRC_WEAPON && wn ? `<small>${wn}</small>` : ''}</div><div class="stats-rb">`;
      if (split) for (let t = 0; t < 2; t++) {
        const tip = `${mode === 'taken' ? `${TEAM[t]} erlitt` : `${TEAM[t]}:`} ${num(v[t])} ${unit} · ${S.name}`;
        s += `<div class="stats-bar" title="${esc(tip)}"><i style="width:${w(v[t])}%;background:${TC[t]}"></i><span>${num(v[t])}</span></div>`;
      } else {
        s += `<div class="stats-bar" title="${esc(`${S.who}: ${num(v[0])} ${unit} an beiden Armeen`)}"><i style="width:${w(v[0])}%;background:${S.c}"></i><span>${S.who} ${num(v[0])}</span></div>`;
      }
      s += '</div></div>';
    }
    M.querySelector('.stats-bars').innerHTML = s;
    M.querySelector('.stats-note').textContent = mode === 'taken'
      ? 'Schaden an Einheiten und Gebäuden des Teams, nach Rüstung.'
      : mode === 'dealt' ? 'Blau = von Blau ausgeteilt, Rot = von Rot. Gott-Werkzeuge und Stürze treffen beide Seiten.'
        : 'Getötete feindliche Einheiten je Quelle.';
  }

  /* heatmap: death cells over a top-down map of the field */
  let base = null, baseKey = [];
  function baseMap(W) {
    const tr = o.terrain(), B = o.buildings() || [];
    if (base && baseKey[0] === W && baseKey[1] === tr && baseKey[2] === B) return base;
    baseKey = [W, tr, B];
    const S = 256, c = document.createElement('canvas'); c.width = c.height = S;
    const g = c.getContext('2d'), img = g.createImageData(S, S), p = img.data;
    const L = tr ? Math.max(1, tr.levels || 1) : 1;
    for (let py = 0; py < S; py++) for (let px = 0; px < S; px++) {
      const x = -W + (px + 0.5) / S * 2 * W, z = -W + (py + 0.5) / S * 2 * W, o4 = (py * S + px) * 4;
      let r = 38, gg = 52, b = 44;
      if (tr) {
        if (!inBounds(tr, x, z)) { r = 16; gg = 20; b = 27; }
        else {
          const i = cellAt(tr, x, z);
          if (tr.water[i] > 0 && !tr.bridge[i]) { r = 28; gg = 58; b = 92; }
          else { const l = tr.level[i] / L; r = 34 + l * 46; gg = 48 + l * 44; b = 40 + l * 34; }
        }
      }
      p[o4] = r; p[o4 + 1] = gg; p[o4 + 2] = b; p[o4 + 3] = 255;
    }
    g.putImageData(img, 0, 0);
    const k = S / (2 * W);
    for (const d of B) {
      g.fillStyle = d.team ? 'rgba(232,90,78,.55)' : 'rgba(90,147,240,.55)';
      g.fillRect((d.x - d.hw + W) * k, (d.z - d.hd + W) * k, Math.max(1.5, d.hw * 2 * k), Math.max(1.5, d.hd * 2 * k));
    }
    return (base = c);
  }
  const heatC = document.createElement('canvas'); heatC.width = heatC.height = HG;
  function heat() {
    const cv = M.querySelector('.stats-map canvas'), g = cv.getContext('2d'), S = cv.width;
    const n = HG * HG, hm = D.heat;
    // zoom to the square around all deaths (both teams, so the view stays put when switching teams)
    let x0 = HG, x1 = -1, z0 = HG, z1 = -1;
    for (let c = 0; c < n; c++) if (hm[c] + hm[n + c]) { const x = c % HG, z = (c / HG) | 0; if (x < x0) x0 = x; if (x > x1) x1 = x; if (z < z0) z0 = z; if (z > z1) z1 = z; }
    let cs = 0, cx = 0, cz = 0;
    if (x1 < 0) { cs = HG; } else {
      cs = Math.min(HG, Math.max(16, x1 - x0 + 5, z1 - z0 + 5));
      cx = Math.min(HG - cs, Math.max(0, Math.round((x0 + x1 + 1 - cs) / 2)));
      cz = Math.min(HG - cs, Math.max(0, Math.round((z0 + z1 + 1 - cs) / 2)));
    }
    const B = baseMap(D.W), bk = B.width / HG;
    g.imageSmoothingEnabled = false;
    g.drawImage(B, cx * bk, cz * bk, cs * bk, cs * bk, 0, 0, S, S);
    // grid lines every 25 / 50 m for scale
    const cell = 2 * D.W / HG, wx0 = -D.W + cx * cell, wz0 = -D.W + cz * cell, span = cs * cell, step = span > 160 ? 50 : 25;
    g.strokeStyle = 'rgba(205,220,255,.08)'; g.lineWidth = 1;
    for (let v = Math.ceil(wx0 / step) * step; v <= wx0 + span; v += step) { const q = Math.round((v - wx0) / span * S) + 0.5; g.beginPath(); g.moveTo(q, 0); g.lineTo(q, S); g.stroke(); }
    for (let v = Math.ceil(wz0 / step) * step; v <= wz0 + span; v += step) { const q = Math.round((v - wz0) / span * S) + 0.5; g.beginPath(); g.moveTo(0, q); g.lineTo(S, q); g.stroke(); }
    M.querySelector('.stats-scale').textContent = `Ausschnitt ${fmt(span)} × ${fmt(span)} m · Linien alle ${step} m`;
    let max = 0;
    const at = c => heatTeam < 0 ? hm[c] + hm[n + c] : hm[heatTeam * n + c];
    for (let c = 0; c < n; c++) { const v = at(c); if (v > max) max = v; }
    const hg = heatC.getContext('2d'), img = hg.createImageData(HG, HG), p = img.data;
    for (let c = 0; c < n; c++) {
      const v = at(c); if (!v) continue;
      const f = Math.sqrt(v / max), o4 = c * 4;
      // one warm hue: dim orange -> bright yellow, more opaque where more fell
      p[o4] = 255; p[o4 + 1] = 90 + f * 150; p[o4 + 2] = 40 + f * 60; p[o4 + 3] = 70 + f * 185;
    }
    hg.putImageData(img, 0, 0);
    g.imageSmoothingEnabled = true; g.imageSmoothingQuality = 'high';
    g.drawImage(heatC, cx, cz, cs, cs, 0, 0, S, S);
    M.querySelector('.stats-max').textContent = max ? `${fmt(max)} pro Feld` : 'keine Gefallenen';
  }

  return { update, reset, show, hide, isOpen: () => open };
}
